import {
  FILTER_FEATURE,
  PROVIDER_MANAGEMENT,
  ROLE_MANAGEMENT_V1,
} from "api/ApiPath";
import { ActionInjection } from "app/actions/di/ActionInjection";
import { ActionUseCase } from "app/actions/domain/useCases/ActionUseCase";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { FilterUseCase } from "app/filters/domain/useCase/FilterUseCase";
import { SystemUserInjection } from "features/systemUserManagement/di/SystemUserInjection";
import { isObject, isUndefined } from "lodash";
import { injectable } from "tsyringe";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { CapabilityEnum } from "utils/enums/CapabilityEnum";
import { DataFormat } from "utils/format/DataFormat";
import { StringFormat } from "utils/format/StringFormat";
import { avoidDuplicate, paramsChecking } from "utils/helpersInTs/helpersInTs";
import { PartnerEntity } from "../entities/PartnerEntity";
import { SystemUserFormRequestEntity } from "../entities/SystemUserFormRequestEntity";
import { SystemUserRequestEntity } from "../entities/SystemUserRequestEntity";
import { SystemUserResponseEntity } from "../entities/SystemUserResponseEntity";
import { ISystemUserRepository } from "../repositories/ISystemUserRepository";

@injectable()
export class SystemUserUseCase {
  private systemUserRepo: ISystemUserRepository;
  private filterUseCase: FilterUseCase;
  private actionUseCase: ActionUseCase;

  constructor() {
    this.systemUserRepo = SystemUserInjection.getRepo();
    this.filterUseCase = FilterInjection.getUseCase();
    this.actionUseCase = ActionInjection.getUseCase();
  }

  private checkParams(
    values: SystemUserRequestEntity,
  ): SystemUserRequestEntity {
    return {
      ...values,
      search: paramsChecking(values?.search, "input"),
      status: paramsChecking(values?.status, "select"),
      role: paramsChecking(values?.role, "select"),
    };
  }

  private updateBody(
    body: SystemUserFormRequestEntity,
    detailInfo: SystemUserResponseEntity,
    action: "create" | "update",
  ) {
    const keysToAvoid = [
      "code",
      "features",
      "referents",
      "configuration",
      "partners",
    ];
    const mappedPartners =
      action === "update" && !!detailInfo?.partners
        ? detailInfo?.partners?.map((item) =>
            StringFormat.parseNoteString(item),
          )
        : [];
    const renderedBody =
      action === "update"
        ? Object.keys(body)?.filter(
            (item) =>
              !Array.isArray(body[item]) &&
              !keysToAvoid?.includes(item) &&
              !isUndefined(
                avoidDuplicate(
                  isObject(detailInfo[item])
                    ? detailInfo[item]?.["code"]
                    : detailInfo[item]?.toString(),
                  body[item]?.toString(),
                ),
              ),
          )
        : Object?.keys(body)?.filter((item) => !keysToAvoid?.includes(item));
    const finalFeatures =
      action === "create"
        ? body?.features
        : this.actionUseCase.buildArrayEditActions(
            body?.features as Array<string>,
            detailInfo?.features,
          );
    const finalReferents =
      action === "create"
        ? body?.referents
        : this.actionUseCase.buildArrayEditActions(
            body?.referents as Array<string>,
            detailInfo?.referents,
          );
    const finalPartners =
      action === "create"
        ? body?.partners
        : body?.partners?.filter((child) =>
            mappedPartners?.some(
              (ele) => ele?.partnerCode !== child?.partnerCode,
            ),
          );
    return {
      ...renderedBody?.reduce(
        (a, v) => ({
          ...a,
          [v]: body[v],
        }),
        {},
      ),
      code: action === "update" ? body?.code : undefined,
      features: finalFeatures,
      referents: finalReferents,
      partners: finalPartners,
      configuration:
        typeof body?.configuration === "string" && body?.configuration
          ? JSON.parse(DataFormat.autoCorrectJSON(String(body.configuration)))
          : body?.configuration,
    } as typeof body;
  }

  async getUsers(
    params: SystemUserRequestEntity,
  ): Promise<ListBaseApiResponseModel<SystemUserResponseEntity>> {
    return await this.systemUserRepo.getList(this.checkParams(params));
  }

  async getSystemUserDetail(
    code: string,
  ): Promise<BaseApiResponseModel<SystemUserResponseEntity>> {
    return await this.systemUserRepo.getDetail(code);
  }

  async createUser(
    body: SystemUserFormRequestEntity,
    detailInfo: SystemUserResponseEntity,
  ): Promise<BaseApiResponseModel<SystemUserResponseEntity>> {
    return await this.systemUserRepo.create(
      this.updateBody(body, detailInfo, "create"),
    );
  }

  async updateUser(
    body: SystemUserFormRequestEntity,
    detailInfo: SystemUserResponseEntity,
  ): Promise<BaseApiResponseModel<SystemUserResponseEntity>> {
    return await this.systemUserRepo.update(
      this.updateBody(body, detailInfo, "update"),
    );
  }

  async deleteUser(
    code: string,
  ): Promise<BaseApiResponseModel<SystemUserResponseEntity>> {
    return await this.systemUserRepo.delete(code);
  }

  async getPartnerList(
    params: BaseFilterListRequestModel,
  ): Promise<Array<PartnerEntity>> {
    return await this.filterUseCase.fetchListWithFeature({
      path: FILTER_FEATURE.PARTNER_LIST,
      isNeedFeature: true,
      params,
      capabilityKey: CapabilityEnum.partnerFilter,
    });
  }

  async getProviderList(
    params: BaseFilterListRequestModel,
  ): Promise<Array<BaseFilterListResponseModel>> {
    return await this.filterUseCase.fetchListWithFeature({
      path: PROVIDER_MANAGEMENT.FILTER_LIST,
      savedKey: CacheKeyEnum.provider,
      needExpired: true,
      isNeedFeature: false,
      params,
    });
  }

  async getRoleList(
    params: BaseFilterListRequestModel,
  ): Promise<Array<BaseFilterListResponseModel>> {
    return await this.filterUseCase.fetchListWithFeature<
      BaseFilterListRequestModel,
      BaseFilterListResponseModel
    >({
      path: ROLE_MANAGEMENT_V1?.FILTER,
      params,
    });
  }
}
